import React, { useState, useRef, useEffect } from 'react';
import { Bookmark, BookmarkCheck } from 'lucide-react';
import { useMapStore } from '../store/mapStore';
import { STATUS } from '../types';
import Filters from './Filters';

const HomePage = () => {
  const { recentlyViewed, setSelectedLocation, setSearchVisible, searchHistory,getLocationId,  isFavoritesVisible, serverFavoritesRaw, isFavorite, toggleFavorite, favoritesIds, favoriteKeyFor, showBookmarksOnly, setShowBookmarksOnly } = useMapStore();
  const [isDragging, setIsDragging] = useState(false);
  const [startY, setStartY] = useState(0);
  const [currentHeight, setCurrentHeight] = useState(50);
  const homePageRef = useRef(null);

  const getMarkerIcon = (status, business_unit) => {
    // If it's not US Broadline, it's considered Specialty — return early
    // console.log("map controls selected status: ", status)
    if (business_unit !== 'US Broadline' && status?.toLowerCase() !== 'target' && business_unit !== '') {
      return '/markers/specialty-marker.svg';
    }

    // For US Broadline, use switch on status
    switch (status?.toUpperCase()) {
      case 'ACTIVE':
        return '/markers/active-marker.svg';
      case 'ACTIVE_OLDER':
        return '/markers/active-older.svg';
      case 'ACTIVEOLDER':
        return '/markers/active-older.svg';
      case 'PROSPECT':
        return '/markers/prospect-marker.svg';
      case 'TARGET':
        return '/markers/target-marker.svg';
      default:
        return '/markers/specialty-marker.svg';
    }
  };
  const handleLocationClick = (location) => {
    console.log("Mobile recent selected location:", location)
    setSelectedLocation(location);
    setSearchVisible(true);
  };

  const sortedHistory = React.useMemo(() => {
  const ts = (x) => new Date(x?.timestamp || x?.createdAt || 0).getTime();
  return [...(searchHistory || [])].sort((a, b) => ts(b) - ts(a));
}, [searchHistory]);

  

    // Build a merged favorites list (rich details from recentlyViewed, fill gaps with backend)
    const favoritesList = React.useMemo(() => {
      const ids = Array.isArray(favoritesIds) ? favoritesIds : [];
      const byId = new Map();

      // Prefer richer objects we already have locally
      recentlyViewed.forEach((loc) => {
        const key = (typeof favoriteKeyFor === 'function'
          ? favoriteKeyFor(loc)
          : (loc?.accountDetails?.accountId || loc?.accountId || loc?.id || loc?.placeId || null));
        if (key && ids.includes(key)) {
          byId.set(key, loc);
        }
      });

      // Fill any gaps from server favorites
      (serverFavoritesRaw || []).forEach((f) => {
        const key = getLocationId(f).id ||
                    (f?.accountName ? `name:${f.accountName.toLowerCase()}` : null);
        if (!key || byId.has(key)) return;
        byId.set(key, {
          accountId: getLocationId(f).id || null,
          name: f?.accountName || '',
          address: f?.address || '',
          lat: f?.latitude ?? null,
          lng: f?.longitude ?? null,
          status: f?.shipStatus || f?.accountStatus || f?.status || '',
          shipStatus: (f?.accountStatus || '').toUpperCase(),
          scope: f?.accountType || f?.scope ||'',
        });
      });

      return Array.from(byId.values());
    }, [recentlyViewed, serverFavoritesRaw, favoritesIds, favoriteKeyFor]);

  useEffect(() => {
    const handleTouchStart = (e) => {
      setIsDragging(true);
      setStartY(e.touches[0].clientY);
    };

    const handleTouchMove = (e) => {
      if (!isDragging) return;
      const deltaY = startY - e.touches[0].clientY;
      const vh = Math.min(Math.max(20, currentHeight + (deltaY / window.innerHeight) * 100), 90);
      setCurrentHeight(vh);
      if (homePageRef.current) {
        homePageRef.current.style.height = `${vh}vh`;
      }
    };

    const handleTouchEnd = () => {
      setIsDragging(false);
      let targetHeight;
      if (currentHeight < 30) targetHeight = 20;
      else if (currentHeight < 70) targetHeight = 50;
      else targetHeight = 90;
      
      setCurrentHeight(targetHeight);
      if (homePageRef.current) {
        homePageRef.current.style.height = `${targetHeight}vh`;
      }
    };

    const homePage = homePageRef.current;
    if (homePage) {
      homePage.addEventListener('touchstart', handleTouchStart);
      document.addEventListener('touchmove', handleTouchMove);
      document.addEventListener('touchend', handleTouchEnd);
    }

    return () => {
      if (homePage) {
        homePage.removeEventListener('touchstart', handleTouchStart);
        document.removeEventListener('touchmove', handleTouchMove);
        document.removeEventListener('touchend', handleTouchEnd);
      }
    };
  }, [isDragging, startY, currentHeight]);

  return (
    <div 
      ref={homePageRef}
      className="fixed inset-x-0 bg-white z-50 rounded-t-2xl overflow-hidden"
      style={{ 
        top: 'auto',
        bottom: '60px',
        height: `${currentHeight}vh`
      }}
    >
      <div className="flex justify-center p-2 cursor-grab touch-none border-b border-gray-100">
        <img 
          src="assets/mobile-assets/drag-icon.svg" 
          alt="Drag handle"
          className="h-6 w-8"
        />
      </div>

      <div className="overflow-y-auto overflow-x-hidden h-full">
        <div className="p-4">
          {isFavoritesVisible ? (
            /* Saved (Favorites) */
            <section className="mb-8">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-fluid-xl font-bold">Bookmarks</h2>
                <label className="flex items-center gap-2 text-fluid-sm">
                  <span className="text-gray-600">Show only</span>
                  <input
                    type="checkbox"
                    checked={!!showBookmarksOnly}
                    onChange={(e) => {
                      console.log('[HomePage] bookmarksOnly ->', e.target.checked);
                      setShowBookmarksOnly(e.target.checked);
                    }}
                    onClick={(e) => e.stopPropagation()}  // avoids interfering with sheet drag
                    className="h-4 w-4 accent-blue-600"
                    aria-label="Show only bookmarked locations"
                  />
                </label>
              </div>

              <div className="space-y-3">
                {favoritesList.map((location) => (
                  <div
                    key={location.accountId || location.id || location.name}
                    className="flex items-center justify-between p-4 bg-white rounded-xl border border-gray-100 shadow-sm"
                    onClick={() => handleLocationClick(location)}
                  >
                    <div className="flex items-center">
                      <div className="mr-3">
                        <img
                          src={getMarkerIcon(
                            location.shipStatus || location.accountStatus,
                            location?.accountDetails?.businessUnit ?? ''
                          )}
                          alt="Location marker"
                          className="w-6 h-6"
                        />
                      </div>
                      <div>
                        <h3 className="font-semibold">{location.name}</h3>
                        {location.address ? (
                          <p className="text-fluid-sm text-gray-500">{location.address}</p>
                        ) : null}
                      </div>
                    </div>

                    {/* favorite toggle */}
                    <button
                      type="button"
                      className="p-2 -m-2 rounded-full hover:bg-gray-100 focus:outline-none"
                      aria-pressed={isFavorite(location)}
                      aria-label={isFavorite(location) ? 'Remove from favorites' : 'Save to favorites'}
                      onPointerDown={(e) => { e.stopPropagation(); e.preventDefault(); }}
                      onMouseDown={(e) => { e.stopPropagation(); e.preventDefault(); }}
                      onTouchStart={(e) => { e.stopPropagation(); e.preventDefault(); }}
                      onClick={(e) => {
                        e.stopPropagation();
                        e.preventDefault();
                        toggleFavorite(location);
                      }}
                    >
                      {isFavorite(location)
                        ? <BookmarkCheck className="w-6 h-6 text-blue-600" />
                        : <Bookmark className="w-6 h-6 text-gray-400" />
                      }
                    </button>
                  </div>
                ))}
                {favoritesList.length === 0 && (
                  <div className="text-center py-8 text-gray-500">
                    <p>No saved accounts</p>
                  </div>
                )}
              </div>
            </section>
          ) : (
            <>
              {/* Recent Section (UNCHANGED) */}
              <section className="mb-8">
                <h2 className="text-fluid-xl font-bold mb-4">Recent</h2>
                <div className="space-y-3">
                  {recentlyViewed.map((location) => (
                    <div
                      key={location.accountId}
                      className="flex items-center justify-between p-4 bg-white rounded-xl border border-gray-100 shadow-sm"
                      onClick={() => handleLocationClick(location)}
                    >
                      <div className="flex items-center">
                        <div className="mr-3">
                          <img
                            src={getMarkerIcon(location.shipStatus || location.accountStatus, location?.accountDetails?.businessUnit ?? '')}
                            alt="Location marker"
                            className="w-6 h-6"
                          />
                        </div>
                        <div>
                          <h3 className="font-semibold">{location.name}</h3>
                          <p className="text-fluid-sm text-gray-500">{location.address}</p>
                        </div>
                      </div>
                      <button
                        type="button"
                        className="p-2 -m-2 rounded-full hover:bg-gray-100 focus:outline-none"
                        aria-pressed={isFavorite(location)}
                        aria-label={isFavorite(location) ? 'Remove from favorites' : 'Save to favorites'}
                        onPointerDown={(e) => { e.stopPropagation(); e.preventDefault(); }}
                        onMouseDown={(e) => { e.stopPropagation(); e.preventDefault(); }}
                        onTouchStart={(e) => { e.stopPropagation(); e.preventDefault(); }}
                        onClick={(e) => {
                          e.stopPropagation();
                          e.preventDefault();
                          toggleFavorite(location);
                        }}
                      >
                        {isFavorite(location)
                          ? <BookmarkCheck className="w-6 h-6 text-blue-600" />
                          : <Bookmark className="w-6 h-6 text-gray-400" />
                        }
                      </button>
                    </div>
                  ))}
                  {recentlyViewed.length === 0 && (
                    <div className="text-center py-8 text-gray-500">
                      <p>No recent locations</p>
                    </div>
                  )}
                </div>
              </section>

              {/* Search History (UNCHANGED) */}
              <section className="mb-8">
                <h2 className="text-fluid-xl font-bold mb-4">Search History</h2>
                <div className="space-y-3">
                  {sortedHistory.map((entry) => (
                    <div
                      key={entry.id}
                      className="flex justify-between items-center p-4 bg-gray-50 rounded-xl border border-gray-200 shadow-sm"
                      onClick={() => useMapStore.getState().replaySearch(entry)}

                    >
                      <div>
                        <h3 className="font-medium text-fluid-sm text-gray-700">{entry.term}</h3>
                        <p className="text-fluid-xs text-gray-400">{new Date(entry.timestamp).toLocaleString()}</p>
                      </div>
                      <span className="text-fluid-xs text-blue-500">Search</span>
                    </div>
                  ))}
                  {searchHistory.length === 0 && (
                    <div className="text-center py-8 text-gray-500">
                      <p>No previous searches</p>
                    </div>
                  )}
                </div>
              </section>
            </>
          )}

        </div>
      </div>
    </div>
  );
};

export default HomePage;